import { useMemo } from 'react';

import { FieldSelect } from '@/components/looker/field-select';
import { useMe } from '@/hooks/use-me';
import { ListTasks } from '@/task-manager/api/queries/list-tasks';
import { TaskCardType } from '@/task-manager/entities/task-card-type';

export type VehicleFilterProps = {
  filters: ListTasks.Filters;
  value?: string;
  onChange: (value?: string) => void;
};

export function VehicleFilter({
  filters,
  value,
  onChange,
}: VehicleFilterProps) {
  const me = useMe();
  const listTasks = ListTasks.useQuery(
    { filters: { ...filters, groupId: undefined, topId: undefined } },
    {
      refetchInterval: 30_000,
      keepPreviousData: true,
    },
  );
  const vehicleIds = useMemo(() => {
    if (!listTasks.data) {
      return [];
    }
    const vehicleIdsFromTasks = listTasks.data.tasks
      .filter(({ cardType }) => cardType === TaskCardType.ALERT)
      .map(({ title }) => title);

    const vehicleIdsFromSuggestions = listTasks.data.alertSuggestions.map(
      ({ frontendId }) => frontendId,
    );

    return Array.from(
      new Set(
        [...vehicleIdsFromTasks, ...vehicleIdsFromSuggestions]
          .flatMap((id) => (id.includes('/') ? id.split('/') : id))
          .map((id) => id.trim()),
      ),
    ).sort();
  }, [listTasks.data]);

  const selectedVehicleId = useMemo<string | undefined>(
    () =>
      vehicleIds.find((fullFrontendId) => fullFrontendId === value) ??
      undefined,
    [value, vehicleIds],
  );

  return (
    <FieldSelect
      label={me.config.top.labels.id}
      placeholder={`Filter by ${me.config.top.labels.id}`}
      value={selectedVehicleId}
      isFilterable={true}
      options={vehicleIds.map((fullFrontendId) => ({
        value: fullFrontendId,
      }))}
      onChange={(vehicleId) => onChange(vehicleId)}
      isClearable={true}
    />
  );
}
